import clsx from "clsx";
import { graphql } from "gatsby";
import React, { FC, ReactNode, useCallback, useRef } from "react";

import { useScrollContainer } from "../../../hooks/use-scroll-container";
import { Container } from "../../container";
import { Icon } from "../../icon";
import { TableSpecialValues } from "./types";

export interface TableSectionProps extends Queries.TableSectionDataFragment {}

const commonIconClassName = "w-9 h-9 mx-auto inline";
const scrollSpeed = 10;

export const TableSection: FC<TableSectionProps> = ({ title, data }) => {
  const [headersRow, ...dataRows] = data?.data?.split("\n") ?? [];
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const {
    canScroll: shouldShowArrows,
    canScrollBackward: canScrollLeft,
    canScrollForward: canScrollRight,
    startScrolling,
    stopScrolling,
  } = useScrollContainer(tableContainerRef, "horizontal");

  const getRowItems = useCallback((row: string) => {
    return row.split(";").map((cellItem) => {
      const specialValues = Object.values(TableSpecialValues).join("|");
      const parts: ReactNode[] = cellItem
        .split(new RegExp(`(^${specialValues})*(${specialValues})`, "g"))
        .filter(Boolean)
        .map((part, i) => {
          switch (part) {
            case TableSpecialValues.Check:
              return <Icon key={i} icon="check-circle" className={commonIconClassName} />;
            case TableSpecialValues.Clear:
              return <Icon key={i} icon="close-circle" className={commonIconClassName} />;
            case TableSpecialValues.Newline:
              return <br key={i} />;
            default:
              return (
                <span key={i} className="py-2 whitespace-nowrap">
                  {part}
                </span>
              );
          }
        });
      return parts;
    });
  }, []);

  const startScrollLeft = useCallback(() => startScrolling(-scrollSpeed), [startScrolling]);
  const startScrollRight = useCallback(() => startScrolling(scrollSpeed), [startScrolling]);

  const headerCells = getRowItems(headersRow);

  return (
    <Container className="flex flex-col gap-5 bg-neutral my-10 lg:my-16" pagePaddingX>
      <div className="relative">
        {/* Title */}
        {title && <h4 className="text-primary text-2xl lg:text-4xl pr-24 pb-4">{title}</h4>}

        {/* Mobile Scroll Arrows */}
        {shouldShowArrows && (
          <div className="text-primary flex items-center absolute right-0 bottom-5 gap-4">
            <Icon
              onMouseDown={startScrollLeft}
              onMouseUp={stopScrolling}
              onTouchStart={startScrollLeft}
              onTouchEnd={stopScrolling}
              icon="chevron-left"
              className={clsx(
                "w-6 transition-all",
                canScrollLeft ? "cursor-pointer" : "opacity-20"
              )}
            />
            {/* TODO: check why chevron right and left are not aligned (different sizes?) */}
            <Icon
              onMouseDown={startScrollRight}
              onMouseUp={stopScrolling}
              onTouchStart={startScrollRight}
              onTouchEnd={stopScrolling}
              icon="chevron-left"
              className={clsx(
                "w-6 transition-all -scale-x-100",
                canScrollRight ? "cursor-pointer" : "opacity-20"
              )}
            />
          </div>
        )}
      </div>

      {/* Table */}
      {/* Upper div is necessary to round the corners, child div manage overflow */}
      <div className="rounded-xl overflow-hidden border-2 border-primary border-opacity-40">
        <div className="overflow-x-auto overflow-y-hidden no-scrollbar" ref={tableContainerRef}>
          <table className="w-full bg-light">
            {/* Table head */}
            <thead>
              <tr>
                {headerCells.map((headerCell, i) => {
                  return (
                    <th
                      key={i}
                      className={clsx(
                        i === 0 ? "text-left" : "text-center",
                        "bg-primary bg-opacity-30 p-2 lg:px-3 lg:py-4 font-bold",
                        "border-primary border-opacity-40",
                        i < headerCells.length - 1 && "border-r"
                      )}
                    >
                      {headerCell}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table body */}
            <tbody>
              {dataRows.map((dataRow, rowIndex) => {
                const rowCells = getRowItems(dataRow);
                return (
                  <tr key={rowIndex}>
                    {rowCells.map((dataCell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={clsx(
                          cellIndex === 0 ? "text-left" : "text-center",
                          "px-2 lg:px-3 py-2 border-opacity-40 border-primary font-light",
                          rowIndex < dataRows.length - 1 && "border-b",
                          cellIndex < rowCells.length - 1 && "border-r"
                        )}
                      >
                        {dataCell}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Container>
  );
};

export const query = graphql`
  fragment TableSectionData on ContentfulTableSection {
    title
    data {
      data
    }
  }
`;
